import type { APIClient } from '@src/shared/api';
import { useMutation } from '@src/shared/query';
import type { AbsenceLock, AbsenceLockDto } from '../../types';
import {
  absenceLockHeartbeatUrl,
  absenceLockReleaseUrl,
  absenceLockUrl,
} from './constants';

type LockPayload = {
  acti_user: number;
  session_id: string;
  /** The live socket claiming this lock, so closing the tab releases it. */
  connection_id?: string;
};

type LockReleasePayload = {
  acti_user: number;
  session_id: string;
  /** Release this acquisition specifically, not whatever the session holds
   * now -- which may be a newer one over the same row. */
  lease?: number;
};

function selectAbsenceLock(lock: AbsenceLockDto): AbsenceLock {
  return {
    lease: lock.lease,
    userId: lock.target_user,
    ownerUserId: lock.owner_user,
    ownerName: lock.owner_name,
    expiresAt: lock.expires_at,
    heartbeatIntervalSeconds: lock.heartbeat_interval_seconds,
    ttlSeconds: lock.ttl_seconds,
  };
}

export default function useAbsenceLock() {
  const { mutateAsync: acquireLock } = useMutation<
    AbsenceLock,
    Error,
    LockPayload
  >({
    mutateFn: async ({
      apiClient,
      ...rest
    }: { apiClient: APIClient } & LockPayload) => {
      const response = (await apiClient.request({
        url: absenceLockUrl,
        method: 'POST',
        payload: rest,
      })) as AbsenceLockDto;
      return selectAbsenceLock(response);
    },
  });

  const { mutateAsync: refreshLock } = useMutation<
    AbsenceLock,
    Error,
    LockReleasePayload
  >({
    mutateFn: async ({
      apiClient,
      ...rest
    }: { apiClient: APIClient } & LockReleasePayload) => {
      const response = (await apiClient.request({
        url: absenceLockHeartbeatUrl,
        method: 'POST',
        payload: rest,
      })) as AbsenceLockDto;
      return selectAbsenceLock(response);
    },
  });

  const { mutateAsync: releaseLock } = useMutation<
    { released: boolean },
    Error,
    LockReleasePayload
  >({
    mutateFn: ({
      apiClient,
      ...rest
    }: { apiClient: APIClient } & LockReleasePayload) => {
      return apiClient.request({
        url: absenceLockReleaseUrl,
        method: 'POST',
        payload: rest,
      }) as Promise<{ released: boolean }>;
    },
  });

  return {
    acquireLock,
    refreshLock,
    releaseLock,
  };
}
